import env from '../config/environments';

/**
 * The public page for a certificate: the certificate site (a separate hosting
 * target) serves it at the certificate id, and that id is the only handle —
 * it is what a verifier is given and what the QR-less link carries.
 * @param {string} certificateId - Public certificate handle
 * @returns {string} - Absolute url of the public certificate page
 */
export const getPublicCertificateUrl = (certificateId) =>
    `${env.certificateSiteUrl}/${certificateId}`;

/**
 * The server prints every score through common-module `printed_score` and sends a
 * whole number, which is shown as is. Anything else is rounded the same way (half up,
 * clamped to 0–100) so the page never disagrees with the PDF; no number is "—", never 0.
 * @param {number} score - A transcript's overall or row score
 * @returns {string} - The score as it is printed
 */
export const formatScore = (score) => {
    if (typeof score !== 'number' || !Number.isFinite(score)) return '—';
    if (Number.isInteger(score)) return String(score);
    return String(Math.min(100, Math.max(0, Math.floor(score + 0.5))));
};

/**
 * Only a transcript carries a score. A participation certificate says the trial was
 * completed, and a record written before `kind` existed is one.
 * @param {Object} certificate - The certificate as the API serves it
 * @returns {boolean} - Whether its score may be shown
 */
export const showsScore = (certificate) =>
    certificate?.kind === 'transcript'
    && typeof certificate.score === 'number'
    && !Number.isNaN(certificate.score);

/**
 * @param {Object} certificate - The certificate as the API serves it
 * @returns {boolean} - Whether it was awarded with honours
 */
export const hasHonours = (certificate) => certificate?.honours === true;

/**
 * @param {Object} certificate - The certificate as the API serves it
 * @returns {boolean} - Whether the record is a transcript rather than a certificate
 */
export const isTranscript = (certificate) => certificate?.kind === 'transcript';

/**
 * The document is named after the structure's `certificate_name` ("Certificate of
 * Financial Analysis I", "Transcript of Financial Analysis I"); without one it is just
 * "Certificate" or "Transcript".
 * @param {Object} certificate - The certificate as the API serves it
 * @returns {string} - The document's name
 */
export const certificateKindLabel = (certificate) => {
    const word = isTranscript(certificate) ? 'Transcript' : 'Certificate';
    const name = typeof certificate?.certificate_name === 'string' ? certificate.certificate_name.trim() : '';
    return name ? `${word} of ${name}` : word;
};

/**
 * A transcript's printed rows, in print order, read defensively: a row without a name
 * is dropped, and a score that is not a number is `null` ("Not assessed"), never 0.
 * @param {Object} certificate - The certificate as the API serves it
 * @returns {{name: string, definition: string, score: (number|null)}[]}
 */
export const transcriptItems = (certificate) => {
    if (!isTranscript(certificate) || !Array.isArray(certificate.items)) return [];
    return certificate.items.flatMap((item) => {
        const name = typeof item?.name === 'string' ? item.name.trim() : '';
        if (!name) return [];
        return [{
            name,
            definition: typeof item.definition === 'string' ? item.definition.trim() : '',
            score: typeof item.score === 'number' && Number.isFinite(item.score) ? item.score : null,
        }];
    });
};

const sameCohort = (a, b) => (
    (Boolean(a.cycle_id) && a.cycle_id === b.cycle_id)
    || (Boolean(a.work_trial_id) && a.work_trial_id === b.work_trial_id)
);

/**
 * The list as the server ordered it, except that a transcript moves to sit directly
 * after the certificate from the same cohort. The student's API serves `work_trial_id`
 * (one trial per student per cohort) rather than `cycle_id`, so either one pairs them.
 * A transcript whose certificate is not in the list keeps its own place.
 * @param {Object[]} certificates - The certificates as the API serves them
 * @returns {Object[]} - The same records, in display order
 */
export const orderCertificates = (certificates) => {
    const rows = Array.isArray(certificates) ? certificates : [];
    const paired = new Set();
    const followers = new Map();

    rows.forEach((row) => {
        if (isTranscript(row)) return;
        const after = rows.filter(other => (
            isTranscript(other) && !paired.has(other) && sameCohort(row, other)
        ));
        after.forEach(other => paired.add(other));
        followers.set(row, after);
    });

    return rows.flatMap((row) => {
        if (paired.has(row)) return [];
        return [row, ...(followers.get(row) || [])];
    });
};
